import { Api, ErrorCode } from "./rest-api";
import { Api as ApiCMS } from "./rest-api-cms";

import { AxiosError, InternalAxiosRequestConfig } from "axios";
import { getErrorMessage } from "./error-messages";

import { toast } from "@onair/minimal-ui/snackbar";
import { decryptAES256, encryptAES256, getKey } from "@onair/utils/crypto";
import isArray from "lodash/isArray";
import { getSession } from "next-auth/react";

const API_URL = new URL(process.env.NEXT_PUBLIC_API_URL as string).origin;
const WEB_URL = process.env.NEXT_PUBLIC_WEB_URL;
const SECRET = process.env.NEXT_PUBLIC_REQUEST_ENCRYPTION_SECRET || "onairdev";
const KEY = getKey(SECRET);

export const restRepository = new Api({
	baseURL: API_URL,
	withCredentials: true,
});

// TODO:: Vu Linh fix - should not duplicate code
export const handleAxiosResponseError = async (
	error: AxiosError<{ message: string; code: string }>,
) => {
	const originalRequest: InternalAxiosRequestConfig & {
		_retry?: boolean;
	} = error?.config!;

	const errorCode = error.response?.data?.code;
	let message =
		error.response?.data?.message ||
		error.message ||
		"Đã có sự cố xảy ra, vui lòng thử lại sau.";

	if (errorCode) {
		message = getErrorMessage(errorCode, message);
	}

	if (process.env.NEXT_PUBLIC_APP_ENV !== "production") {
		console.error("DEV ~ Call API Error:", {
			message,
			path: error.request?.path,
		});
	}

	if (error && error?.status == 403) {
		if (typeof window !== "undefined" && error.config?.method === "get") {
			toast.error("Bạn không có quyền truy cập trang này");
			const url = new URL(window.location.href);
			url.searchParams.set("_error", "403");
			window.history.replaceState({}, "", url.toString());
			return Promise.reject(error);
		}

		if (error.config?.method !== "get") {
			toast.error(
				error.response?.data.code === (ErrorCode?.NOT_OWNER || "NOT_OWNER")
					? "Bạn không phải là chủ sở hữu của tài nguyên này"
					: "Bạn không có quyền thực hiện hành động này",
			);
			return Promise.reject(error);
		}
	}

	if (
		error.response &&
		error.response.status === 401 &&
		!originalRequest._retry
	) {
		originalRequest._retry = true;

		let sessionUrl = `${WEB_URL}/api/auth/session`;
		if (typeof window !== "undefined") {
			sessionUrl = `${window.location.origin}/api/auth/session`;
		}

		// This will trigger the refresh token function in the next-auth config we configured
		await restRepository.instance.get(sessionUrl);

		return restRepository.instance(originalRequest);
	}

	const ctMessage =
		message === "Network Error"
			? "Không có kết nối mạng, vui lòng thử lại"
			: message;
	if (typeof window !== "undefined") {
		toast.dismiss(error.request?.path);
		if (typeof ctMessage === "string") {
			toast.error(ctMessage, {
				id: error.request?.path ?? "--",
			});
		}

		if (isArray(ctMessage)) {
			ctMessage.forEach((m: any, index: number) => {
				if (m.errorCode === "INVALID_PRODUCT_ITEM") {
					return toast.error(m.message);
				}
				toast.error(m, {
					id: error?.config?.url ? `${error?.config?.url}-${index}` : "--",
				});
			});
		}
	}

	return Promise.reject(error);
};

restRepository.instance.interceptors.response.use(
	(res) => res,
	handleAxiosResponseError,
);

restRepository.instance.interceptors.request.use(async (config) => {
	// const session = await getSession();
	// if (session?.accessToken) {
	// 	config.headers.Authorization = `Bearer ${session?.accessToken}`;
	// }

	const encryptBody = !!process.env.NEXT_PUBLIC_REQUEST_ENCRYPTION;

	if (!encryptBody) {
		return config;
	}

	if (config.data instanceof FormData) {
		return config;
	}

	if (config.data && typeof config.data === "object") {
		config.headers["x-encrypt"] = "aes256";
		config.data = encryptAES256(config.data, KEY);
	}

	return config;
});
restRepository.instance.interceptors.response.use((response) => {
	const headers = response.headers;
	if (typeof response.data === "string" && headers["x-encrypt"] === "aes256") {
		try {
			response.data = decryptAES256(response.data, KEY);
		} catch (err: any) {
			console.warn("Failed to decrypt response:", err.message);
		}
	}
	return response;
});

export const restCmsRepository = new ApiCMS({
	baseURL: process.env.NEXT_PUBLIC_CMS_URL,
	withCredentials: false,
});

restCmsRepository.instance.interceptors.response.use(
	(res) => res,
	handleAxiosResponseError,
);
